import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { UserRegistration as UserRegistrationType, UserRole } from '../../types/user';
import { registerUser } from '../../services/userService';
import { counties, subCounties, facilities, County, SubCounty, Facility } from '../../data/locations';

const initialState: UserRegistrationType = {
  name: '',
  email: '',
  password: '',
  role: UserRole.FACILITY_USER,
  county: '',
  subCounty: '',
  facility: ''
};

const UserRegistration: React.FC = () => {
  const [formData, setFormData] = useState<UserRegistrationType>(initialState);
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  
  const navigate = useNavigate();
  
  // Filter subcounties based on selected county
  const filteredSubCounties = formData.county 
    ? subCounties.filter((sc: SubCounty) => sc.countyName === formData.county)
    : [];
    
  // Filter facilities based on selected subcounty
  const filteredFacilities = formData.subCounty
    ? facilities.filter((f: Facility) => f.subCountyName === formData.subCounty)
    : [];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Reset dependent fields when parent field changes
    if (name === 'county') {
      setFormData({ ...formData, county: value, subCounty: '', facility: '' });
    } else if (name === 'subCounty') {
      setFormData({ ...formData, subCounty: value, facility: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };
  
  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      role: e.target.value as UserRole
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match
    if (formData.password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Reset states
    setError(null);
    setLoading(true);
    
    try {
      await registerUser(formData);
      setSuccess(true);
      
      // Redirect after a delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Register User
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Registration successful! Redirecting to dashboard...</Alert>}
      
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ width: '100%' }}>
          <TextField
            required
            fullWidth
            label="Full Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
          />
        </Box>
        
        <Box sx={{ width: '100%' }}>
          <TextField
            required
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              required
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              inputProps={{ minLength: 6 }}
            />
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <TextField
              required
              fullWidth
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              inputProps={{ minLength: 6 }}
            />
          </Box>
        </Box>
        
        <Box sx={{ width: '100%' }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Role</FormLabel>
            <RadioGroup value={formData.role} onChange={handleRoleChange} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <FormControlLabel 
                value={UserRole.CQI_MANAGER} 
                control={<Radio />} 
                label="CQI Manager (Admin)" 
              />
              <FormControlLabel 
                value={UserRole.NATIONAL_USER} 
                control={<Radio />} 
                label="National User (All Counties)" 
              />
              <FormControlLabel 
                value={UserRole.COUNTY_USER} 
                control={<Radio />} 
                label="County User (Specific County)" 
              />
              <FormControlLabel 
                value={UserRole.FACILITY_USER} 
                control={<Radio />} 
                label="Facility User (Specific Facility)" 
              />
            </RadioGroup>
          </FormControl>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {/* Only show county field for County and Facility users */}
          {(formData.role === UserRole.COUNTY_USER || formData.role === UserRole.FACILITY_USER) && (
            <TextField
              select
              fullWidth
              label="County"
              name="county"
              value={formData.county || ''}
              onChange={handleChange}
              required={formData.role === UserRole.COUNTY_USER || formData.role === UserRole.FACILITY_USER}
            >
              <MenuItem value="">
                <em>Select County</em>
              </MenuItem>
              {counties.map((county) => (
                <MenuItem key={county.name} value={county.name}>
                  {county.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          
          {/* Only show sub-county field for Facility users */}
          {formData.role === UserRole.FACILITY_USER && (
            <TextField
              select
              fullWidth
              label="Sub-County"
              name="subCounty"
              value={formData.subCounty || ''}
              onChange={handleChange}
              disabled={!formData.county}
              required={formData.role === UserRole.FACILITY_USER}
            >
              <MenuItem value="">
                <em>Select Sub-County</em>
              </MenuItem>
              {filteredSubCounties.map((subCounty) => (
                <MenuItem key={subCounty.name} value={subCounty.name}>
                  {subCounty.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          
          {/* Only show facility field for Facility users */}
          {formData.role === UserRole.FACILITY_USER && (
            <TextField
              select
              fullWidth
              label="Facility"
              name="facility"
              value={formData.facility || ''}
              onChange={handleChange}
              disabled={!formData.subCounty}
              required={formData.role === UserRole.FACILITY_USER}
            >
              <MenuItem value="">
                <em>Select WIT/CHU</em>
              </MenuItem>
              {filteredFacilities.map((facility) => (
                <MenuItem key={facility.name} value={facility.name}>
                  {facility.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
        
        <Box sx={{ width: '100%', mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Register'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default UserRegistration;
